import type { Context, Plugin } from '@segment/analytics-next'
import { getOneTrustConsent, transformOneTrustConsent } from '@susu/cookie'
import { getOptionalLocaleFromPathname } from '@susu/locale-utils'

import { debugLogEvent, isAnalyticsDebugEnabled } from './debug'
import { isBrowser } from './env'
import { getDeclaredPageType, getPageCommonProperties } from './page-context'
import { createGAdata } from './utils'

/**
 * Derives the Segment page `path` from a locale-prefixed pathname.
 * Homepage (`/en-us/`) returns the locale (`en-us`).
 * Other pages (`/en-us/cart`) return the path after the locale (`cart`).
 */
function getSegmentPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length <= 1) {
    return segments[0] ?? ''
  }
  return segments.slice(1).join('/')
}

// Matched case-insensitively. Customer Hub in-store puts the customer's email
// in `customerProfileEmail`; email links to the preference center carry the
// rest. Marketing click IDs (utm_*, gclid, …) are not in this list and stay.
const PERSONAL_QUERY_PARAMS = new Set([
  'customerprofileemail',
  'email',
  'emailaddress',
  'email-uid',
  'email-signedcode',
  'user-id',
])

const isPersonalParam = (pair: string): boolean => {
  const key = pair.split('=')[0] ?? ''
  try {
    return PERSONAL_QUERY_PARAMS.has(decodeURIComponent(key).toLowerCase())
  } catch {
    return PERSONAL_QUERY_PARAMS.has(key.toLowerCase())
  }
}

/**
 * Removes personal query params from a URL, a path or a search string. Works
 * on the raw text so the params that stay keep their exact encoding.
 */
function withoutPersonalParams<T>(value: T): T {
  if (typeof value !== 'string') {
    return value
  }
  const queryStart = value.indexOf('?')
  if (queryStart === -1) {
    return value
  }
  const hashStart = value.indexOf('#', queryStart)
  const queryEnd = hashStart === -1 ? value.length : hashStart
  const kept = value
    .slice(queryStart + 1, queryEnd)
    .split('&')
    .filter(pair => !isPersonalParam(pair))
  return `${value.slice(0, queryStart)}${kept.length ? `?${kept.join('&')}` : ''}${value.slice(queryEnd)}` as T
}

/**
 * Creates a Segment enrichment plugin that adds consent, locale, page context,
 * and common properties (including marketing click IDs from @susu/segment-common).
 * SSR-safe.
 * @returns Segment Plugin instance configured for Suitsupply GA integration
 */
export const integrationPlugin = (): Plugin => {
  let previousUrl: string | undefined

  const withConsentContext = (ctx: Context): Context => {
    if (!isBrowser()) {
      return ctx
    }

    const url = new URL(window.location.href)
    const href = withoutPersonalParams(url.href)
    const referrer = withoutPersonalParams(previousUrl ?? document.referrer)
    previousUrl = href

    ctx.updateEvent('context', {
      ...ctx.event.context,
      consent: transformOneTrustConsent(getOneTrustConsent()),
      locale: getOptionalLocaleFromPathname(url.pathname),
      page: {
        ...ctx.event.context?.page,
        path: getSegmentPath(url.pathname),
        referrer,
        search: withoutPersonalParams(url.search),
        url: href,
      },
    })

    // Page-level values, the same for every application on the page. The page
    // owner completes them where the page says nothing (a path without a
    // locale, an in-store customer); a composed application cannot.
    const common = getPageCommonProperties()
    const properties: Record<string, unknown> = {
      ...ctx.event.properties,
      ...common,
      // page_type is page-level: the page owner's declared one wins. Until an
      // owner declares it, a page_type the app set (e.g. storefront's
      // store_overview/store_details, which the path alone can't tell apart)
      // still wins over the path-based default.
      page_type:
        getDeclaredPageType() ??
        (ctx.event.properties?.page_type || common.page_type),
    }
    // `url` is archived in the tracking plan as an event property, and
    // context.page.url carries it. Page calls keep `properties.url` (the
    // warehouse's pages.url) as segment-common's locale-stripped url.
    if (ctx.event.type === 'track') {
      delete properties.url
    }
    for (const key of ['url', 'path', 'search', 'referrer']) {
      if (key in properties) {
        properties[key] = withoutPersonalParams(properties[key])
      }
    }
    ctx.updateEvent('properties', properties)

    if (isAnalyticsDebugEnabled()) {
      debugLogEvent(
        ctx.event.type,
        ctx.event.event ?? ctx.event.name,
        ctx.event,
      )
    }

    return ctx
  }

  return {
    name: 'Suitsupply GA Integration Plugin',
    type: 'enrichment',
    version: '1',
    isLoaded: () => true,
    identify: withConsentContext,
    load: createGAdata,
    page: withConsentContext,
    track: withConsentContext,
  }
}
